import type { AssociationType } from '@/lib/crm/core/SchemaClient';

import {
  clearAllPersistedCacheByPrefix,
  getPersistedCache,
  setPersistedCache,
} from './persistedCache';

// v2: Added `source_excluded_entity_type_names` and `target_excluded_entity_type_names`
// fields. Bumping the version forces a refetch so cached entries pick them up;
// without exclusions, kind-based matchers can't honor "kind X except type Y" rules
// (e.g. note-to-person excludes ahoy_user) and would briefly include excluded types.
const CACHE_VERSION = 2;
const STORAGE_KEY_PREFIX = 'ahoy-cache-association-types-';
const COMPONENT = 'persisted-association-types';

function buildKey(orgId: number | string): string {
  return `${STORAGE_KEY_PREFIX}${orgId}`;
}

function isAssociationTypeShape(item: unknown): boolean {
  if (typeof item !== 'object' || item === null) {
    return false;
  }
  const a = item as Record<string, unknown>;
  const validCardinality =
    a.cardinality === 'one_to_one' ||
    a.cardinality === 'one_to_many' ||
    a.cardinality === 'many_to_one' ||
    a.cardinality === 'many_to_many';
  const validReadAccess =
    a.value_read_access === 'all' || a.value_read_access === 'internal';
  const validWriteAccess =
    a.value_write_access === undefined ||
    a.value_write_access === 'all' ||
    a.value_write_access === 'internal';
  const validTransitiveVia =
    a.transitive_via === undefined ||
    a.transitive_via === null ||
    (Array.isArray(a.transitive_via) &&
      a.transitive_via.every(v => typeof v === 'string'));
  const validSourceKind =
    a.source_entity_kind_name === undefined ||
    a.source_entity_kind_name === null ||
    typeof a.source_entity_kind_name === 'string';
  const validTargetKind =
    a.target_entity_kind_name === undefined ||
    a.target_entity_kind_name === null ||
    typeof a.target_entity_kind_name === 'string';
  return (
    typeof a.association_type_name === 'string' &&
    typeof a.label === 'string' &&
    typeof a.source_entity_type_name === 'string' &&
    typeof a.target_entity_type_name === 'string' &&
    typeof a.reverse_association_type_name === 'string' &&
    validCardinality &&
    validReadAccess &&
    validWriteAccess &&
    validTransitiveVia &&
    validSourceKind &&
    validTargetKind
  );
}

function isAssociationTypesShape(raw: unknown): raw is AssociationType[] {
  return Array.isArray(raw) && raw.every(isAssociationTypeShape);
}

export function getPersistedAssociationTypes(
  orgId: number | string | undefined
): AssociationType[] | undefined {
  if (orgId === undefined) {
    return undefined;
  }
  return getPersistedCache<AssociationType[]>(
    buildKey(orgId),
    CACHE_VERSION,
    isAssociationTypesShape,
    COMPONENT
  );
}

export function setPersistedAssociationTypes(
  orgId: number | string,
  types: AssociationType[]
): void {
  setPersistedCache(buildKey(orgId), types, CACHE_VERSION, COMPONENT);
}

export function clearAllPersistedAssociationTypes(): void {
  clearAllPersistedCacheByPrefix(STORAGE_KEY_PREFIX, COMPONENT);
}
